/* Collections and their spots: the reads both pages start from, and the writes that make a
   collection the user's own.

   A collection's spots are fetched once and kept in the browser under the collection's id,
   stamped with the `updated_at` they were read at. The database moves that stamp whenever
   any spot of the collection changes, so one small request — the list of collections —
   tells the client whether 500 cached spots are still good. Offline, the cached list and
   the cached spots are what the app runs on. */

import {
  DEFAULT_OPPONENTS,
  PROFILES_MAX,
  opponentsSchema,
  readOpponents,
  rowFromSpot,
  spotFromRow,
  spotRowSchema,
  z,
  type Opponent,
  type Spot,
  type SpotRow,
} from "@preflop/core";
import type { KV } from "./kv";

export const collectionRowSchema = z.object({
  id: z.uuid(),
  owner_id: z.uuid().nullable(),
  is_base: z.boolean(),
  name: z.string(),
  description: z.string(),
  visibility: z.enum(["private", "public"]),
  forked_from: z.uuid().nullable(),
  spot_count: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
  // Absent from a list cached by a build older than publishing, or than moderation.
  published_at: z.string().nullable().optional(),
  moderated_at: z.string().nullable().optional(),
  moderation_note: z.string().optional(),
  // Likewise for a list cached before opponent profiles. Read loosely on the way in — a
  // profile this build cannot make sense of is dropped, and the collection still opens.
  opponents: z.unknown().optional(),
});
export type CollectionRow = z.infer<typeof collectionRowSchema>;

/** A published collection as anyone signed in may see it: of its owner, the display name and
    nothing else. Read from the `public_collections` view. */
export const publicCollectionRowSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  spot_count: z.number().int(),
  stakes: z.array(z.string()),
  formats: z.array(z.string()),
  published_at: z.string(),
  updated_at: z.string(),
  owner_name: z.string(),
  copies: z.number().int(),
  mine: z.boolean(),
  is_base: z.boolean(),
});

export interface PublicCollection {
  id: string;
  name: string;
  description: string;
  spotCount: number;
  stakes: string[];
  formats: string[];
  publishedAt: string;
  updatedAt: string;
  ownerName: string;
  /** How many accounts have copied it. */
  copies: number;
  /** Whether it is the caller's own. */
  mine: boolean;
  /** The base ranges: nobody's, signed by the app, managed by its administrators. */
  isBase: boolean;
}

export const publicCollectionFromRow = (raw: unknown): PublicCollection => {
  const r = publicCollectionRowSchema.parse(raw);
  return {
    id: r.id,
    name: r.name,
    description: r.description,
    spotCount: r.spot_count,
    stakes: r.stakes,
    formats: r.formats,
    publishedAt: r.published_at,
    updatedAt: r.updated_at,
    ownerName: r.owner_name,
    copies: r.copies,
    mine: r.mine,
    isBase: r.is_base,
  };
};

export interface Collection {
  id: string;
  ownerId: string | null;
  isBase: boolean;
  name: string;
  description: string;
  visibility: "private" | "public";
  forkedFrom: string | null;
  spotCount: number;
  createdAt: string;
  updatedAt: string;
  /** When it was made public; null while it is private. */
  publishedAt: string | null;
  /** When an administrator took it down; null when none did. While set, it cannot be
      published. */
  moderatedAt: string | null;
  /** What the administrator said about it, for its owner. */
  moderationNote: string;
  /** The kinds of opponent its charts may be written against. Born with the four defaults;
      the owner's to add to, rename, recolour and remove. */
  opponents: Opponent[];
}

const collectionFromRow = (r: CollectionRow): Collection => ({
  id: r.id,
  ownerId: r.owner_id,
  isBase: r.is_base,
  name: r.name,
  description: r.description,
  visibility: r.visibility,
  forkedFrom: r.forked_from,
  spotCount: r.spot_count,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
  publishedAt: r.published_at ?? null,
  moderatedAt: r.moderated_at ?? null,
  moderationNote: r.moderation_note ?? "",
  // A row from a build or a cache that predates variations has no column: the four defaults
  // stand, which is what the database would have given it.
  opponents: r.opponents === undefined ? [...DEFAULT_OPPONENTS] : readOpponents(r.opponents),
});

/** What a user may say about a collection, with the limits the table checks. */
export const NAME_MAX = 80;
export const DESCRIPTION_MAX = 2000;
export const collectionInputSchema = z.object({
  name: z.string().trim().min(1, "Give the collection a name.").max(NAME_MAX),
  description: z.string().trim().max(DESCRIPTION_MAX).default(""),
  /** Left out, the list stays as it is: renaming a collection is not a way to lose it. */
  opponents: opponentsSchema.optional(),
});
export type CollectionInput = z.input<typeof collectionInputSchema>;

/** A name nobody has to think of: the stem, or the first of "stem 2", "stem 3"… not taken. */
export function freshName(taken: readonly string[], stem: string): string {
  const used = new Set(taken.map((n) => n.trim().toLowerCase()));
  for (let n = 1; ; n++) {
    const name = n === 1 ? stem : `${stem} ${n}`;
    if (!used.has(name.toLowerCase())) return name;
  }
}

/** The chart of a spot as a row carries it, without the ids that belong to where it lives. */
export type SpotContent = Pick<SpotRow, "key" | "meta" | "actions" | "cells"> & {
  copied_from: string | null;
};

/** A refusal by the database that has a name: a Postgres error code, and what it said. */
export class DataError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = "DataError";
  }
}

/** Where rows come from and go to. The Supabase client in the app, a stub in tests. Writes
    throw a `DataError` when the database refuses them. */
export interface Source {
  collections(): Promise<unknown[]>;
  spots(collectionId: string): Promise<unknown[]>;
  createCollection(row: { owner_id: string; name: string; description: string }): Promise<unknown>;
  updateCollection(
    id: string,
    patch: { name?: string; description?: string; opponents?: Opponent[] },
  ): Promise<unknown>;
  deleteCollection(id: string): Promise<void>;
  /** Make a collection public, or private again. Resolves to the row as written. */
  setVisibility(id: string, visibility: "public" | "private"): Promise<unknown>;
  /** Rows of `public_collections`: the caller's own only, or everybody's. */
  publicCollections(only: "mine" | "all"): Promise<unknown[]>;
  /** The whole collection, in one transaction, by the database. Resolves to the copy's id. */
  copyCollection(sourceId: string, name: string): Promise<string>;
  /** Resolves to the row as written. Refused with a unique violation when the collection
      already holds the key. */
  insertSpot(collectionId: string, spot: SpotContent): Promise<unknown>;
  /** The chart of the spot with this key, and its origin only when one is given. Resolves to
      the row as written, or null when the collection holds no such spot. */
  updateSpot(
    collectionId: string,
    key: string,
    chart: Pick<SpotContent, "meta" | "actions" | "cells"> & { copied_from?: string | null },
  ): Promise<unknown | null>;
  deleteSpot(collectionId: string, key: string): Promise<void>;
}

export interface SaveOptions {
  /** The spot this chart was copied from. Left out, an updated chart keeps the origin it had
      and a new one has none. */
  copiedFrom?: string | null;
  /** What to do when the collection already holds the spot. "replace", the default, is an
      edit; "refuse" writes nothing and says so, for a caller that wants to ask first. */
  ifExists?: "replace" | "refuse";
}

export type Saved =
  | { status: "inserted" | "updated"; spot: Spot }
  /** Only with `ifExists: "refuse"`: the collection holds the spot, and nothing was written. */
  | { status: "exists" };

export interface LoadedSpots {
  spots: Spot[];
  /** "network" when the rows were just fetched, "cache" when the stamp still matched, and
      "stale" when the network failed and an older copy is what there was. */
  from: "network" | "cache" | "stale";
}

export interface CollectionsApi {
  /** Every collection this session may read, the base collection first. */
  listCollections(): Promise<Collection[]>;
  loadSpots(collection: Pick<Collection, "id" | "updatedAt">): Promise<LoadedSpots>;

  createCollection(ownerId: string, input: CollectionInput): Promise<Collection>;
  updateCollection(id: string, input: CollectionInput): Promise<Collection>;
  deleteCollection(id: string): Promise<void>;
  /** Publish a collection of the user's own: anyone signed in may then see it, drill it and
      copy it, under the owner's display name. The database wants a chart in it and a display
      name on the profile, and `dataMessage` says which was missing. */
  publish(id: string): Promise<Collection>;
  /** Private again, at once. Copies already made are their owners' and stay. */
  unpublish(id: string): Promise<Collection>;
  /** The caller's own published collections, as everyone sees them: with their copies. */
  myPublished(): Promise<PublicCollection[]>;
  /** A collection of the user's own holding every chart of the source. Resolves to its id. */
  copyCollection(sourceId: string, name: string): Promise<string>;
  /** One chart into a collection of the user's own, remembering where it came from. "exists"
      when the collection already holds that spot and `replace` was not asked for: nothing
      was written. A variation carries its profile: `profile` is what the source collection
      calls it, and a target whose list does not know it is given it, name and note, so the
      copy says what it is where it lands. */
  copySpot(
    targetId: string,
    spot: Spot,
    options?: { replace?: boolean; profile?: Opponent | null },
  ): Promise<"copied" | "replaced" | "exists">;
  /** Write one chart into a collection of the user's own: over the spot with its key when the
      collection holds one, as a new spot when it does not. */
  saveSpot(collectionId: string, spot: Spot, options?: SaveOptions): Promise<Saved>;
  deleteSpot(collectionId: string, key: string): Promise<void>;
}

const UNIQUE_VIOLATION = "23505";
const CHECK_VIOLATION = "23514";

/** A database refusal as a sentence a page can show. Anything unexpected passes through. */
export function dataMessage(error: unknown): string {
  if (error instanceof z.ZodError) return error.issues[0]?.message ?? "That is not valid.";
  if (error instanceof DataError) {
    if (error.code === CHECK_VIOLATION && /at most 50 collections/.test(error.message))
      return "You have reached the limit of 50 collections. Delete one to make room.";
    if (error.code === CHECK_VIOLATION && /spot_count/.test(error.message))
      return "A collection holds at most 2,000 charts.";
    if (error.code === "P0002") return "That collection is no longer there to copy.";
    if (error.code === CHECK_VIOLATION && /opponents/.test(error.message))
      return `A collection holds at most ${PROFILES_MAX} opponent profiles, each with a name, one of the eight colours and a short note.`;
    if (error.code === CHECK_VIOLATION && /at most 20 reports/.test(error.message))
      return "That is 20 reports in a day, which is the most one account files. Thank you; try again tomorrow.";
    if (error.code === CHECK_VIOLATION && /taken down by a moderator/.test(error.message))
      return "This collection was taken down by a moderator and cannot be published. Write to privacy@altruia.ca if you think that is a mistake.";
    if (error.code === "42501" && /Administrators only/.test(error.message))
      return "That is for administrators only.";
    if (error.code === "P0002" && /take down|taken down/.test(error.message))
      return "That collection is no longer there, or no longer taken down.";
    if (error.code === CHECK_VIOLATION && /always public/.test(error.message))
      return "The base ranges are always public.";
    if (error.code === CHECK_VIOLATION && /needs a chart/.test(error.message))
      return "An empty collection cannot be published: put a chart in it first.";
    if (error.code === CHECK_VIOLATION && /display name is needed to publish/.test(error.message))
      return "Choose a display name on the account page first: a public collection says whose it is.";
    if (error.code === CHECK_VIOLATION && /while a collection is public/.test(error.message))
      return "A display name is needed while one of your collections is public. Unpublish first.";
  }
  return error instanceof Error ? error.message : String(error);
}

interface CachedSpots {
  updatedAt: string;
  rows: SpotRow[];
}

const LIST_KEY = "list";
const spotsKey = (id: string): string => `spots:${id}`;

export function createCollectionsApi(source: Source, cache: KV): CollectionsApi {
  /* Any spot write moves the collection's `updated_at`, which is what the cached rows are
     stamped with: left alone, one saved chart would make the next load fetch every row again.
     So the row just written is patched into the cache and the cache restamped — but only when
     the collection's new stamp equals the row's. Both are the writing transaction's now(), so
     equality means ours was the last write; anything else, and the cached rows are dropped
     for the next load to fetch. */
  async function patchCache(collectionId: string, written: SpotRow | null): Promise<void> {
    const kept = await cache.get<CachedSpots>(spotsKey(collectionId));
    if (!kept) return;
    try {
      const list = z.array(collectionRowSchema).parse(await source.collections());
      await cache.set(LIST_KEY, list);
      const stamp = list.find((c) => c.id === collectionId)?.updated_at;
      if (written && stamp && stamp === written.updated_at) {
        const rows = kept.rows.filter((r) => r.key !== written.key).concat(written);
        rows.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
        return await cache.set(spotsKey(collectionId), { updatedAt: stamp, rows });
      }
    } catch {
      // The write itself succeeded; a cache that cannot be patched is simply not kept.
    }
    await cache.delete(spotsKey(collectionId));
  }

  /* A variation says what it is a variation of, so the profile travels with the chart: a
     target collection whose list does not know it is given it, name, colour and note, before
     the chart lands. A list already at its limit is left alone — the chart is what was asked
     for, and a copy that says `vs nit` by its id beats no copy at all. */
  async function teachProfile(collectionId: string, profile: Opponent): Promise<void> {
    const list = z.array(collectionRowSchema).parse(await source.collections());
    const target = list.find((c) => c.id === collectionId);
    if (!target) return;
    const known = readOpponents(target.opponents ?? [...DEFAULT_OPPONENTS]);
    if (known.some((o) => o.id === profile.id) || known.length >= PROFILES_MAX) return;
    await source.updateCollection(collectionId, { opponents: [...known, profile] });
    await cache.delete(LIST_KEY);
  }

  async function saveSpot(
    collectionId: string,
    spot: Spot,
    { copiedFrom, ifExists = "replace" }: SaveOptions = {},
  ): Promise<Saved> {
    // Through rowFromSpot, so what is sent is what a row may hold, whatever the page held.
    const { key, meta, actions, cells } = rowFromSpot(spot);
    const origin = copiedFrom === undefined ? {} : { copied_from: copiedFrom };
    const done = async (status: "inserted" | "updated", raw: unknown): Promise<Saved> => {
      const row = spotRowSchema.parse(raw);
      await patchCache(collectionId, row);
      return { status, spot: spotFromRow(row) };
    };

    if (ifExists === "replace") {
      const updated = await source.updateSpot(collectionId, key, {
        meta,
        actions,
        cells,
        ...origin,
      });
      if (updated) return done("updated", updated);
    }
    try {
      const inserted = await source.insertSpot(collectionId, {
        key,
        meta,
        actions,
        cells,
        copied_from: copiedFrom ?? null,
      });
      return await done("inserted", inserted);
    } catch (e) {
      if (e instanceof DataError && e.code === UNIQUE_VIOLATION && ifExists === "refuse")
        return { status: "exists" };
      throw e;
    }
  }

  return {
    async listCollections() {
      let rows: CollectionRow[];
      try {
        rows = z.array(collectionRowSchema).parse(await source.collections());
        await cache.set(LIST_KEY, rows);
      } catch (e) {
        const kept = await cache.get<CollectionRow[]>(LIST_KEY);
        if (!kept) throw e;
        rows = kept;
      }
      return rows
        .map(collectionFromRow)
        .sort((a, b) => Number(b.isBase) - Number(a.isBase) || a.name.localeCompare(b.name));
    },

    async loadSpots({ id, updatedAt }) {
      const kept = await cache.get<CachedSpots>(spotsKey(id));
      if (kept && kept.updatedAt === updatedAt)
        return { spots: kept.rows.map(spotFromRow), from: "cache" };
      try {
        // Parsed on the way in too: a row this build cannot read is a bug to hear about, not
        // a chart to draw wrong.
        const rows = z.array(spotRowSchema).parse(await source.spots(id));
        await cache.set(spotsKey(id), { updatedAt, rows } satisfies CachedSpots);
        return { spots: rows.map(spotFromRow), from: "network" };
      } catch (e) {
        if (!kept) throw e;
        return { spots: kept.rows.map(spotFromRow), from: "stale" };
      }
    },

    /* The writes leave the cache alone but for one thing: a deleted collection's spots go.
       Everything else corrects itself, because the list is read from the network first and a
       collection whose `updated_at` moved is fetched again. */

    async createCollection(ownerId, input) {
      const { name, description } = collectionInputSchema.parse(input);
      const row = await source.createCollection({ owner_id: ownerId, name, description });
      return collectionFromRow(collectionRowSchema.parse(row));
    },

    async updateCollection(id, input) {
      const { name, description, opponents } = collectionInputSchema.parse(input);
      const row = await source.updateCollection(id, {
        name,
        description,
        // Left out of the input, the column is left alone rather than written empty.
        ...(opponents ? { opponents } : {}),
      });
      return collectionFromRow(collectionRowSchema.parse(row));
    },

    async deleteCollection(id) {
      await source.deleteCollection(id);
      await cache.delete(spotsKey(id));
    },

    async publish(id) {
      const row = await source.setVisibility(id, "public");
      return collectionFromRow(collectionRowSchema.parse(row));
    },

    async unpublish(id) {
      const row = await source.setVisibility(id, "private");
      return collectionFromRow(collectionRowSchema.parse(row));
    },

    async myPublished() {
      return (await source.publicCollections("mine")).map(publicCollectionFromRow);
    },

    async copyCollection(sourceId, name) {
      return source.copyCollection(sourceId, collectionInputSchema.parse({ name }).name);
    },

    async copySpot(targetId, spot, { replace = false, profile = null } = {}) {
      if (spot.profile && profile?.id === spot.profile) await teachProfile(targetId, profile);
      const { status } = await saveSpot(targetId, spot, {
        copiedFrom: spot.id ?? null,
        ifExists: replace ? "replace" : "refuse",
      });
      return status === "inserted" ? "copied" : status === "updated" ? "replaced" : "exists";
    },

    saveSpot,

    async deleteSpot(collectionId, key) {
      await source.deleteSpot(collectionId, key);
      await patchCache(collectionId, null);
    },
  };
}
